<template>
  <v-card id="webhook-list">
    <v-card-title class="align-start">
      <span>Вебхуки</span>
      <v-spacer></v-spacer>
      <v-btn
        outlined
        small
        :loading="loading"
        @click="fetch()"
      >
        <v-icon
          size="18"
          class="me-2"
        >
          {{ icons.mdiRefresh }}
        </v-icon>
        <span>Обновить</span>
      </v-btn>
    </v-card-title>

    <v-card-subtitle class="pb-0">
      Вебхуки внешних систем так, как их принял отдельный сервис: что пришло, от кого и
      что с этим стало. Тело открывается нажатием на строку.
    </v-card-subtitle>

    <v-card-text
      v-if="loaded && !error && receiverAvailable"
      class="pt-5 pb-0"
    >
      <div class="d-flex flex-wrap webhook-list__totals">
        <v-chip
          small
          label
          :outlined="!!filters.status"
          @click="showAll"
        >
          Все: {{ allCount }}
        </v-chip>
        <v-chip
          v-for="total in statusTotals"
          :key="total.status"
          small
          label
          :outlined="filters.status !== total.status"
          :color="total.color"
          class="v-chip-light-bg"
          :class="`${total.color}--text`"
          @click="toggleStatus(total)"
        >
          {{ total.statusName }}: {{ total.count }}
        </v-chip>
      </div>
    </v-card-text>

    <v-card-text class="d-flex align-center flex-wrap pt-5 webhook-list__filters">
      <v-select
        v-if="sources.length > 1"
        v-model="filters.source"
        :items="sourceItems"
        dense
        outlined
        hide-details
        label="Источник"
        class="webhook-list__source"
      ></v-select>

      <v-menu
        v-model="datesOpened"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="datesLabel"
            readonly
            dense
            outlined
            hide-details
            clearable
            label="Период"
            :prepend-inner-icon="icons.mdiCalendarRange"
            class="webhook-list__period"
            v-bind="attrs"
            v-on="on"
            @click:clear="clearDates"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="dates"
          range
          locale="ru"
          first-day-of-week="1"
          @change="applyDates"
        ></v-date-picker>
      </v-menu>

      <v-text-field
        v-model="filters.search"
        single-line
        dense
        outlined
        clearable
        hide-details
        placeholder="Поиск по телу вебхука..."
        class="webhook-list__search"
      ></v-text-field>
    </v-card-text>

    <v-card-text
      v-if="error || receiverDown"
      class="pt-0"
    >
      <v-alert
        v-if="error"
        type="error"
        dense
        text
        class="mb-0"
      >
        {{ error }}
      </v-alert>

      <v-alert
        v-else
        text
        color="warning"
        class="mb-0"
      >
        Приёмник вебхуков не ответил или не подключён. Пока он молчит, сказать, что к
        нему приходило, нельзя.
      </v-alert>
    </v-card-text>

    <v-data-table
      v-else
      :headers="headers"
      :items="webhooks"
      :server-items-length="totalCount"
      :loading="loading"
      :page="filters.pageNumber"
      :items-per-page="filters.pageSize"
      :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
      :no-data-text="emptyText"
      disable-sort
      class="text-no-wrap"
      @update:options="paginate"
      @click:row="open"
    >
      <template #[`item.receivedAt`]="{item}">
        {{ toDateTimeString(item.receivedAt) }}
      </template>

      <template #[`item.status`]="{item}">
        <v-chip
          small
          label
          :color="statusColor(item.status)"
          class="v-chip-light-bg"
          :class="`${statusColor(item.status)}--text`"
        >
          {{ item.statusName }}
        </v-chip>
      </template>

      <template #[`item.nextAttemptAt`]="{item}">
        {{ toDateTimeString(item.nextAttemptAt) || '—' }}
      </template>

      <template #[`item.processedAt`]="{item}">
        {{ toDateTimeString(item.processedAt) || '—' }}
      </template>

      <template #[`item.error`]="{item}">
        <div
          v-if="item.error"
          class="webhook-list__error text-truncate error--text"
        >
          {{ item.error }}
        </div>
        <span v-else>—</span>
      </template>
    </v-data-table>

    <v-dialog
      :value="!!details"
      max-width="900"
      scrollable
      @input="closeDetails"
    >
      <v-card v-if="details">
        <v-card-title class="d-flex align-center flex-wrap">
          <v-chip
            small
            label
            :color="statusColor(details.status)"
            class="v-chip-light-bg me-3"
            :class="`${statusColor(details.status)}--text`"
          >
            {{ details.statusName }}
          </v-chip>
          <span class="me-3">{{ details.source }}</span>
          <span class="text--secondary me-3">{{ details.event }}</span>
          <span class="text-body-2 text--secondary">{{ toDateTimeString(details.receivedAt) }}</span>
        </v-card-title>

        <v-card-text>
          <v-simple-table
            dense
            class="mb-6"
          >
            <tbody>
              <tr
                v-for="row in detailsRows"
                :key="row.title"
              >
                <td class="text--secondary webhook-list__title">
                  {{ row.title }}
                </td>
                <td>{{ row.value }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <template v-if="details.error">
            <p class="font-weight-medium text--primary mb-2">
              Ошибка последней попытки
            </p>
            <div class="webhook-list__details-error mb-6">
              {{ details.error }}
            </div>
          </template>

          <p class="font-weight-medium text--primary mb-2">
            Тело вебхука
          </p>
          <v-progress-linear
            v-if="detailsLoading"
            indeterminate
          ></v-progress-linear>
          <v-alert
            v-else-if="detailsError"
            type="error"
            dense
            text
            class="mb-0"
          >
            {{ detailsError }}
          </v-alert>
          <p
            v-else-if="bodyEmpty"
            class="text--secondary mb-0"
          >
            Тело пустое.
          </p>
          <pre
            v-else
            class="webhook-list__body"
          >{{ body }}</pre>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeDetails"
          >
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mdiCalendarRange, mdiRefresh } from '@mdi/js'
import debounce from 'lodash/debounce'
import { toDateTimeString } from '@/helpers'
import { webhookBody, webhookStatusColor } from '@/helpers/webhooks'

export default {
  name: 'WebhookList',
  async mounted() {
    document.title = 'Вебхуки'
    await this.fetch()
  },
  data: () => ({
    icons: { mdiCalendarRange, mdiRefresh },
    toDateTimeString,
    datesOpened: false,
    dates: [],

    statusName: '',
    filters: {
      status: null,
      source: null,
      search: '',
      dateFrom: null,
      dateTo: null,
      pageNumber: 1,
      pageSize: 25,
    },
  }),
  computed: {
    ...mapState('webhook', {
      webhooks: 'list',
      totals: 'totals',
      sources: 'sources',
      totalCount: 'totalCount',
      receiverAvailable: 'receiverAvailable',
      loading: 'loading',
      loaded: 'loaded',
      error: 'error',
      details: 'details',
      detailsLoading: 'detailsLoading',
      detailsError: 'detailsError',
    }),

    receiverDown() {
      return this.loaded && !this.error && !this.receiverAvailable
    },
    statusTotals() {
      const { status } = this.filters
      const totals = this.totals.map(total => ({ ...total, color: webhookStatusColor(total.status) }))

      if (!status || totals.some(total => total.status === status)) return totals

      return [...totals, {
        status, statusName: this.statusName || status, count: 0, color: webhookStatusColor(status),
      }]
    },
    allCount() {
      return this.totals.reduce((sum, total) => sum + total.count, 0)
    },
    sourceItems() {
      return [{ text: 'Все', value: null }, ...this.sources]
    },

    filtered() {
      const {
        status, source, search, dateFrom,
      } = this.filters

      return Boolean(status || source || search || dateFrom)
    },
    emptyText() {
      return this.filtered ? 'Под фильтр ничего не подошло.' : 'Вебхуков ещё не приходило.'
    },
    datesLabel() {
      const { dateFrom, dateTo } = this.filters

      if (!dateFrom) return ''

      return dateFrom === dateTo ? dateFrom : `${dateFrom} — ${dateTo}`
    },

    headers() {
      return [
        { text: 'Принят', value: 'receivedAt' },
        { text: 'Источник', value: 'source' },
        { text: 'Событие', value: 'event' },
        { text: 'Статус', value: 'status' },
        { text: 'Попыток', value: 'processingAttemptCount', align: 'right' },
        { text: 'Ошибка', value: 'error' },
        { text: 'Следующая попытка', value: 'nextAttemptAt' },
        { text: 'Обработан', value: 'processedAt' },
      ]
    },
    detailsRows() {
      const webhook = this.details

      return [
        { title: 'Номер', value: webhook.id },
        { title: 'Источник', value: webhook.source },
        { title: 'Событие', value: webhook.event },
        { title: 'Тип', value: webhook.type },
        { title: 'Принят', value: toDateTimeString(webhook.receivedAt) },
        { title: 'Попыток обработки', value: webhook.processingAttemptCount },
        { title: 'Следующая попытка', value: toDateTimeString(webhook.nextAttemptAt) || '—' },
        { title: 'Обработан', value: toDateTimeString(webhook.processedAt) || '—' },
      ]
    },
    bodyEmpty() {
      const { payload } = this.details

      return payload === null || payload === undefined || payload === ''
    },
    body() {
      return webhookBody(this.details.payload)
    },
  },
  watch: {
    'filters.search': debounce(function search() {
      this.resetAndFetch()
    }, 400),
    'filters.status': 'resetAndFetch',
    'filters.source': 'resetAndFetch',
  },
  methods: {
    ...mapActions('webhook', ['fetchList', 'openDetails', 'closeDetails']),
    statusColor(status) {
      return webhookStatusColor(status)
    },
    async fetch() {
      await this.fetchList(this.filters)
    },
    async resetAndFetch() {
      this.filters.pageNumber = 1
      await this.fetch()
    },

    async paginate({ page, itemsPerPage }) {
      if (page === this.filters.pageNumber && itemsPerPage === this.filters.pageSize) return

      this.filters.pageNumber = page
      this.filters.pageSize = itemsPerPage
      await this.fetch()
    },
    showAll() {
      this.statusName = ''
      this.filters.status = null
    },
    toggleStatus(total) {
      const selected = this.filters.status === total.status

      this.statusName = selected ? '' : total.statusName
      this.filters.status = selected ? null : total.status
    },

    applyDates(range) {
      const [first, second] = [...range].sort()

      this.filters.dateFrom = first
      this.filters.dateTo = second || first
      this.datesOpened = false
      this.resetAndFetch()
    },
    clearDates() {
      this.dates = []
      this.filters.dateFrom = null
      this.filters.dateTo = null
      this.resetAndFetch()
    },
    async open(row) {
      await this.openDetails(row)
    },
  },
}
</script>

<style lang="scss" scoped>
#webhook-list {
  ::v-deep tbody tr {
    cursor: pointer;
  }
}

.webhook-list__totals {
  gap: 8px;
}

.webhook-list__filters {
  gap: 16px;
}

.webhook-list__source {
  max-width: 220px;
}

.webhook-list__period {
  max-width: 280px;
}

.webhook-list__search {
  max-width: 420px;
}

.webhook-list__error {
  max-width: 15rem;
}

.webhook-list__title {
  width: 220px;
}

.webhook-list__details-error {
  color: var(--v-error-base);
  font-size: 0.8125rem;
  white-space: pre-line;
  word-break: break-word;
}

.webhook-list__body {
  max-height: 320px;
  overflow: auto;
  padding: 12px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.04);
  font-size: 0.8125rem;
  line-height: 1.4;
}

.theme--dark .webhook-list__body {
  background: rgba(255, 255, 255, 0.06);
}
</style>
